import {
  CONTRACT_RESOURCE,
  ConsoleState,
  findDraft,
  FLOW_RESOURCE,
  inferContract,
  kindOf,
  kindRulesResource,
  readPath,
  routeSamples,
  SHARED_KINDS_RESOURCE,
  splitByField,
  suggestSplit,
} from '@casa/domain';

import { compileFlow, lockAddress } from './flow';

/**
 * The first draft of an endpoint whose docs show several kinds of bill: its flow already split
 * the way the docs tell them apart, and each kind's rules started from its own docs example. An
 * endpoint that already has a draft, or whose docs show one kind, starts as it is.
 */
export const startBillKinds = (state: ConsoleState, routeId: string): Record<string, string> | undefined => {
  const route = state.routes.find((item) => item.id === routeId);
  if (!route || findDraft(state, routeId, 'shared')) return undefined;
  const samples = routeSamples(state, routeId, 'shared');
  const spec = suggestSplit(samples);
  if (!spec || spec.branches.length === 0) return undefined;

  const example = (value: string) => {
    for (const sample of samples.filter((item) => item.source === 'docs')) {
      try {
        const payload = JSON.parse(sample.payload) as unknown;
        if (readPath(payload, spec.path) === value) return Array.isArray(payload) ? payload[0] : payload;
      } catch {
        /* an example that is not JSON starts no rules */
      }
    }
    return undefined;
  };
  const { main, ...split } = spec;
  const rules = (value: string) => {
    const bill = example(value);
    return bill === undefined ? undefined : JSON.stringify(inferContract(bill), null, 2);
  };

  const resources: Record<string, string> = {
    [FLOW_RESOURCE]: splitByField(lockAddress(state, route, 'shared', compileFlow(state, route)), split),
    [SHARED_KINDS_RESOURCE]: JSON.stringify({ shared: [main] }, null, 2),
  };
  const mainRules = rules(main);
  if (mainRules) resources[CONTRACT_RESOURCE] = mainRules;
  for (const branch of spec.branches) {
    const branchRules = rules(branch.value);
    if (branchRules) resources[kindRulesResource(kindOf(branch.name))] = branchRules;
  }
  return resources;
};
